import { ProcessingLiveness, PromptRun, PromptRunProcessingStatus, Video } from '../services/api';

export type RunScopedVideoState = 'successful' | 'in_progress' | 'failed' | 'cancelled';

const IN_PROGRESS_STATUSES = new Set([
  'pending',
  'queued',
  'starting',
  'processing',
  'running',
  'in_progress',
  'importing',
  'scanning',
  'uploading',
]);

const TERMINAL_RUN_STATUSES = new Set([
  'cancelled',
  'completed',
  'completed_with_failures',
  'failed',
]);

const normalizeStatus = (status?: string | null): string =>
  String(status || '').trim().toLowerCase();

/** Segment completion and whole-video finalization have separate persisted owners. */
export const getRunProjectionStatus = (projection: PromptRunProcessingStatus): string => {
  const status = normalizeStatus(projection.status);
  const phase = normalizeStatus(projection.activity?.phase);
  const videoLevelStatus = normalizeStatus(projection.video_level?.status);
  if (status === 'cancelled' || phase === 'cancelled') return 'cancelled';
  if (phase === 'failed') return 'failed';
  if (phase && !TERMINAL_RUN_STATUSES.has(phase)) return 'processing';
  if (videoLevelStatus === 'processing') return 'processing';
  if (videoLevelStatus === 'failed' && Number(projection.successful_segments || 0) > 0) return 'completed_with_failures';
  return status;
};

const executionTime = (value?: string): number => {
  const time = value ? Date.parse(value) : 0;
  return Number.isFinite(time) ? time : 0;
};

/** Resolve one execution for a media item; result availability never controls membership. */
export const selectVideoRun = (
  videoId: string,
  runs: readonly PromptRun[],
  selectedRunId?: string | null,
): PromptRun | null => {
  const candidates = runs.filter((run) => (
    Array.isArray(run.run_context?.video_ids)
    && run.run_context.video_ids.includes(videoId)
  ));
  if (selectedRunId) return candidates.find((run) => run.run_id === selectedRunId) || null;
  const ordered = [...candidates].sort((left, right) => (
    executionTime(right.executed_at) - executionTime(left.executed_at)
    || right.run_id.localeCompare(left.run_id)
  ));
  return ordered.find((run) => IN_PROGRESS_STATUSES.has(normalizeStatus(run.status)))
    || ordered.find((run) => TERMINAL_RUN_STATUSES.has(normalizeStatus(run.status)))
    || null;
};

const normalizeVideoDisplayStatus = (status?: string | null): string => {
  const normalized = normalizeStatus(status);
  if (!normalized) return 'not_processed';
  if (normalized === 'completed' || normalized === 'success' || normalized === 'successful') {
    return 'processed';
  }
  if (IN_PROGRESS_STATUSES.has(normalized)) {
    return 'processing';
  }
  if (normalized === 'error') {
    return 'failed';
  }
  return normalized;
};

export const getRunScopedVideoProjection = (
  video?: Video | null,
  runScopeId?: string | null,
): PromptRunProcessingStatus | null => {
  if (!video || !Array.isArray(video.processing_status)) {
    return null;
  }
  if (runScopeId) return video.processing_status.find((entry) => entry.run_id === runScopeId) || null;
  // Lightweight media selectors already carry the authorized per-run projection.
  // Creation time preserves execution order; later status writes do not reorder history.
  const ordered = [...video.processing_status].sort((left, right) => (
    executionTime(right.created_at) - executionTime(left.created_at)
    || right.run_id.localeCompare(left.run_id)
  ));
  return ordered.find(isRunProjectionInProgress)
    || ordered.find((run) => TERMINAL_RUN_STATUSES.has(normalizeStatus(run.status)))
    || null;
};

export const isRunProjectionInProgress = (
  runProjection?: PromptRunProcessingStatus | null,
): boolean => {
  if (!runProjection) return false;
  const normalizedRunStatus = getRunProjectionStatus(runProjection);
  if (TERMINAL_RUN_STATUSES.has(normalizedRunStatus)) return false;
  if (IN_PROGRESS_STATUSES.has(normalizedRunStatus)) return true;
  const videoLevelStatus = normalizeStatus(runProjection.video_level?.status);
  if (IN_PROGRESS_STATUSES.has(videoLevelStatus)) return true;
  if (Number(runProjection.pending_segments || 0) > 0) return true;
  if (Number(runProjection.processing_segments || 0) > 0) return true;
  return Array.isArray(runProjection.segments)
    ? runProjection.segments.some((segment) => IN_PROGRESS_STATUSES.has(normalizeStatus(segment.status)))
    : false;
};

export const getRunScopedVideoState = (
  video?: Video | null,
  runScopeId?: string | null,
  parentRunStatus?: string | null,
): RunScopedVideoState | null => {
  return getRunProjectionState(getRunScopedVideoProjection(video, runScopeId), parentRunStatus);
};

export const getRunProjectionState = (
  runProjection?: PromptRunProcessingStatus | null,
  parentRunStatus?: string | null,
): RunScopedVideoState | null => {
  if (!runProjection) {
    return null;
  }

  const normalizedRunStatus = getRunProjectionStatus(runProjection);
  const videoLevelStatus = normalizeStatus(runProjection.video_level?.status);
  const successfulSegments = Number(runProjection.successful_segments || 0);
  const failedSegments = Number(runProjection.failed_segments || 0);

  if (normalizedRunStatus === 'cancelled') {
    return 'cancelled';
  }
  if (normalizedRunStatus === 'failed' || normalizedRunStatus === 'completed_with_failures') {
    return 'failed';
  }
  if (normalizedRunStatus === 'completed') {
    if (IN_PROGRESS_STATUSES.has(normalizeStatus(parentRunStatus)) && videoLevelStatus === 'processing') {
      return 'in_progress';
    }
    return 'successful';
  }
  if (isRunProjectionInProgress(runProjection)) {
    return 'in_progress';
  }
  if (
    normalizedRunStatus === 'failed'
    || normalizedRunStatus === 'completed_with_failures'
    || videoLevelStatus === 'failed'
    || failedSegments > 0
  ) {
    return 'failed';
  }
  if (
    successfulSegments > 0
    || normalizedRunStatus === 'completed'
    || videoLevelStatus === 'successful'
  ) {
    return 'successful';
  }
  return 'in_progress';
};

export const getDisplayedVideoStatus = (
  video?: Video | null,
  runScopeId?: string | null,
  runStatus?: string | null,
): string => {
  const runScopedState = getRunScopedVideoState(video, runScopeId, runStatus);
  if (runScopedState === 'successful') return 'processed';
  if (runScopedState === 'in_progress') return 'processing';
  if (runScopedState === 'failed') return 'failed';
  if (runScopedState === 'cancelled') return 'cancelled';
  if (runScopeId) return IN_PROGRESS_STATUSES.has(normalizeStatus(runStatus)) ? 'processing' : 'not_processed';
  return normalizeVideoDisplayStatus(video?.status);
};

export const isDisplayedVideoInProgress = (
  video?: Video | null,
  runScopeId?: string | null,
): boolean => {
  if (runScopeId) {
    return getRunScopedVideoState(video, runScopeId) === 'in_progress';
  }
  return IN_PROGRESS_STATUSES.has(normalizeVideoDisplayStatus(video?.status));
};

export interface VideoProgressSummary {
  status: string;
  phase?: string;
  completed?: number;
  total?: number;
  failed?: number;
  unit?: string;
  lastWorkerActivityAt?: string;
}

/** Extraction counts planned parts; accepted segments do not describe work still pending. */
export const getVideoProgressSummary = (
  video?: Video | null,
  runScopeId?: string | null,
  runStatus?: string | null,
  runLiveness?: ProcessingLiveness,
): VideoProgressSummary => {
  const projection = getRunScopedVideoProjection(video, runScopeId);
  const lastWorkerActivityAt = runLiveness?.last_heartbeat_at || projection?.liveness?.last_heartbeat_at;
  if (!projection) return { status: runScopeId ? runStatus || 'not_processed' : video?.status || 'not_processed', lastWorkerActivityAt };
  const status = getRunProjectionStatus(projection);
  const phase = projection.activity?.phase || (projection.video_level?.status === 'processing' ? 'synthesizing' : undefined);
  if (!TERMINAL_RUN_STATUSES.has(status) && ['extracting', 'pending', 'queued'].includes(phase || status)) {
    const failed = Math.max(0, Number(projection.failed_parts) || 0);
    return { status, phase, lastWorkerActivityAt, unit: 'parts',
      completed: Math.max(0, Number(projection.completed_parts) || 0) + failed,
      total: Math.max(0, Number(projection.total_parts) || 0), failed };
  }
  const successful = Math.max(0, Number(projection.successful_segments) || 0);
  const failed = Math.max(0, Number(projection.failed_segments) || 0);
  const total = Math.max(0, Number(projection.total_segments) || 0);
  return {
    status,
    phase,
    lastWorkerActivityAt,
    completed: successful + failed,
    total,
    failed,
  };
};
